import { ReactNode } from "react";
import { Box } from "../../ui/box";
import { Button, ButtonIcon } from "../../ui/button";
import { HStack } from "../../ui/hstack";
import { Text } from "../../ui/text";
import { ChevronLeft, ChevronRight, TrashIcon } from "lucide-react-native";

type TileSize = "sm" | "lg";

type Props = {
  index: number;
  total: number;
  onRemove: () => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  children: ReactNode;
  // Rendered on top of `children` but below the chrome (badge/trash/chevrons)
  // — e.g. a "selected as thumbnail" ring or a loading state.
  overlay?: ReactNode;
  // Forces both chevrons into the disabled state regardless of position —
  // the media gallery tiles use this while an eager upload is in flight, so
  // reordering can't race the index the upload was started against.
  reorderDisabled?: boolean;
  // "sm" (default) is the original 80x80 frame used by `forms/dragFile`
  // consumers — its output must stay byte-identical. "lg" is the larger
  // frame the media gallery uses on its full-screen variant.
  size?: TileSize;
};

const FRAME_CLASSES: Record<TileSize, string> = {
  sm: "w-20 h-20",
  lg: "w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48",
};

const BADGE_CLASSES: Record<TileSize, string> = {
  sm: "w-5 h-5",
  lg: "w-6 h-6",
};

const ACTION_BUTTON_CLASSES: Record<TileSize, string> = {
  sm: "h-6 w-6",
  lg: "h-7 w-7",
};

// The tile frame shared by every reorderable gallery tile: a numbered
// position badge, a trash button, and (once there's more than one tile)
// left/right reorder chevrons. `MultipleDragFile`'s `ImageTile` wraps an
// `Image` in it; the media gallery's video/image tile reuses it around its
// own thumbnail preview.
export default function TileChrome({
  index,
  total,
  onRemove,
  onMoveLeft,
  onMoveRight,
  children,
  overlay,
  reorderDisabled = false,
  size = "sm",
}: Props) {
  const position = index + 1;
  return (
    <Box
      className={`relative ${FRAME_CLASSES[size]} rounded-xl overflow-hidden border border-outline-100`}
    >
      {children}
      {overlay}
      <Box
        className={`absolute top-1 left-1 ${BADGE_CLASSES[size]} rounded-full bg-background-900/70 items-center justify-center`}
      >
        <Text
          size="2xs"
          className="text-typography-0 font-bold"
          accessibilityLabel={`Posición ${position}`}
        >
          {position}
        </Text>
      </Box>
      <Button
        size="circle-sm"
        variant="solid"
        action="negative"
        rounded="full"
        className={`absolute top-1 right-1 ${ACTION_BUTTON_CLASSES[size]}`}
        onPress={onRemove}
        accessibilityLabel={`Eliminar imagen ${position}`}
      >
        <ButtonIcon as={TrashIcon} size="xs" />
      </Button>
      {total > 1 && (
        <HStack className="absolute bottom-1 left-1 right-1 justify-between">
          <Button
            size="circle-sm"
            variant="solid"
            rounded="full"
            className={`bg-background-900/70 ${ACTION_BUTTON_CLASSES[size]}`}
            isDisabled={index === 0 || reorderDisabled}
            onPress={onMoveLeft}
            accessibilityLabel={`Mover imagen ${position} a la izquierda`}
          >
            <ButtonIcon
              as={ChevronLeft}
              size="xs"
              className="text-typography-0"
            />
          </Button>
          <Button
            size="circle-sm"
            variant="solid"
            rounded="full"
            className={`bg-background-900/70 ${ACTION_BUTTON_CLASSES[size]}`}
            isDisabled={index === total - 1 || reorderDisabled}
            onPress={onMoveRight}
            accessibilityLabel={`Mover imagen ${position} a la derecha`}
          >
            <ButtonIcon
              as={ChevronRight}
              size="xs"
              className="text-typography-0"
            />
          </Button>
        </HStack>
      )}
    </Box>
  );
}
